// Admin HPC Report Service
// Fetches pre-generated HPC reports and student data from admin HPC management API
import { API_BASE_URL } from '../../../config';

const getAuthHeaders = () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) throw new Error("Authentication required. Please log in again.");
    return {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
    };
};

// ─── Management: Schools Overview ────────────────────────────────────────────

export const fetchManagementSchools = async () => {
    const response = await fetch(`${API_BASE_URL}/admin/hpc/management/schools`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch management schools: ${response.statusText}`);
    return response.json();
};

// ─── Dashboard Stats ─────────────────────────────────────────────────────────

export const fetchHPCStats = async (schoolId = null) => {
    const params = schoolId ? `?school_id=${schoolId}` : '';
    const response = await fetch(`${API_BASE_URL}/admin/hpc/stats${params}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch HPC stats: ${response.statusText}`);
    return response.json();
};

// ─── Teachers ────────────────────────────────────────────────────────────────

export const fetchTeacherStats = async (params = {}, schoolId = null) => {
    if (schoolId) params = { ...params, school_id: schoolId };
    const query = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/admin/hpc/teachers${query ? `?${query}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch teacher stats: ${response.statusText}`);
    return response.json();
};

export const fetchTeacherDetail = async (teacherId, schoolId = null) => {
    const params = schoolId ? `?school_id=${schoolId}` : '';
    const response = await fetch(`${API_BASE_URL}/admin/hpc/teachers/${teacherId}${params}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch teacher detail: ${response.statusText}`);
    return response.json();
};

// ─── Classes ─────────────────────────────────────────────────────────────────

export const fetchHPCClasses = async (params = {}, schoolId = null) => {
    if (schoolId) params = { ...params, school_id: schoolId };
    const query = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/admin/hpc/classes${query ? `?${query}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch classes: ${response.statusText}`);
    return response.json();
};

export const fetchClassStudents = async (classId, schoolId = null) => {
    const params = schoolId ? `?school_id=${schoolId}` : '';
    const response = await fetch(`${API_BASE_URL}/admin/hpc/classes/${classId}/students${params}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch class students: ${response.statusText}`);
    return response.json();
};

export const fetchClassActivities = async (classId, params = {}, schoolId = null) => {
    if (schoolId) params = { ...params, school_id: schoolId };
    const query = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/admin/hpc/classes/${classId}/activities${query ? `?${query}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch class activities: ${response.statusText}`);
    return response.json();
};

// ─── Students ────────────────────────────────────────────────────────────────

export const fetchAllStudents = async (params = {}, schoolId = null) => {
    if (schoolId) params = { ...params, school_id: schoolId };
    const query = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/admin/hpc/students${query ? `?${query}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch students: ${response.statusText}`);
    return response.json();
};

export const fetchStudentActivities = async (studentId, params = {}, schoolId = null) => {
    if (schoolId) params = { ...params, school_id: schoolId };
    const query = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/admin/hpc/students/${studentId}/activities${query ? `?${query}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch student activities: ${response.statusText}`);
    return response.json();
};

// ─── Report Generation ──────────────────────────────────────────────────────

export const generateHPCReport = async (studentId, selectedActivityIds, includeSchoolBranding = true) => {
    const response = await fetch(`${API_BASE_URL}/admin/hpc/reports/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            student_id: studentId,
            selected_activity_ids: selectedActivityIds,
            include_school_branding: includeSchoolBranding,
        }),
    });
    if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`Failed to generate report: ${errorBody}`);
    }
    return response.json();
};

export const generateBulkHPCReports = async (classId, selectedActivityIds, includeSchoolBranding = true) => {
    const response = await fetch(`${API_BASE_URL}/admin/hpc/reports/generate-bulk`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            class_id: classId,
            selected_activity_ids: selectedActivityIds,
            include_school_branding: includeSchoolBranding,
        }),
    });
    if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`Failed to start bulk generation: ${errorBody}`);
    }
    return response.json();
};

export const getBulkGenerationStatus = async (jobId) => {
    const response = await fetch(`${API_BASE_URL}/admin/hpc/reports/bulk-status/${jobId}`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`Failed to fetch bulk status: ${response.statusText}`);
    return response.json();
};

// ─── Report Retrieval ────────────────────────────────────────────────────────

export const fetchGeneratedReports = async (params = {}, schoolId = null) => {
    if (schoolId) params = { ...params, school_id: schoolId };
    const query = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/admin/hpc/reports${query ? `?${query}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch reports: ${response.statusText}`);
    return response.json();
};

export const fetchStudentReport = async (studentId, schoolId = null) => {
    const params = schoolId ? `?school_id=${schoolId}` : '';
    const response = await fetch(`${API_BASE_URL}/admin/hpc/reports/student/${studentId}${params}`, { headers: getAuthHeaders() });
    if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`Failed to fetch student report: ${response.statusText}`);
    }
    return response.json();
};

export const fetchReportById = async (reportId, schoolId = null) => {
    const params = schoolId ? `?school_id=${schoolId}` : '';
    const response = await fetch(`${API_BASE_URL}/admin/hpc/reports/${reportId}${params}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch report: ${response.statusText}`);
    return response.json();
};

export const deleteReport = async (reportId) => {
    const response = await fetch(`${API_BASE_URL}/admin/hpc/reports/${reportId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`Failed to delete report: ${response.statusText}`);
    return response.json();
};

// ─── School Branding ─────────────────────────────────────────────────────────

export const fetchSchoolBranding = async (schoolId = null) => {
    const params = schoolId ? `?school_id=${schoolId}` : '';
    const response = await fetch(`${API_BASE_URL}/admin/hpc/school-branding${params}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch school branding: ${response.statusText}`);
    return response.json();
};

export const updateSchoolBranding = async (brandingData) => {
    const response = await fetch(`${API_BASE_URL}/admin/hpc/school-branding`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(brandingData),
    });
    if (!response.ok) throw new Error(`Failed to update school branding: ${response.statusText}`);
    return response.json();
};

export const uploadSchoolLogo = async (file, schoolId = null) => {
    const accessToken = localStorage.getItem("accessToken");
    const formData = new FormData();
    formData.append('file', file);
    const params = schoolId ? `?school_id=${schoolId}` : '';
    const response = await fetch(`${API_BASE_URL}/admin/hpc/school-branding/logo${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` },
        body: formData,
    });
    if (!response.ok) {
        let detail = response.statusText;
        try { const err = await response.json(); detail = err.detail || detail; } catch (_) {}
        throw new Error(detail);
    }
    return response.json();
};

export const uploadPrincipalSignature = async (file, schoolId = null) => {
    const accessToken = localStorage.getItem("accessToken");
    const formData = new FormData();
    formData.append('file', file);
    const params = schoolId ? `?school_id=${schoolId}` : '';
    const response = await fetch(`${API_BASE_URL}/admin/hpc/school-branding/signature${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` },
        body: formData,
    });
    if (!response.ok) {
        let detail = response.statusText;
        try { const err = await response.json(); detail = err.detail || detail; } catch (_) {}
        throw new Error(detail);
    }
    return response.json();
};

// ─── Assignment Detail & Submissions (HPC drill-down) ────────────────────────

export const fetchAssignmentDetail = async (assignmentId, schoolId = null) => {
    const params = schoolId ? `?school_id=${schoolId}` : '';
    const response = await fetch(`${API_BASE_URL}/admin/hpc/assignments/${assignmentId}${params}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch assignment detail: ${response.statusText}`);
    return response.json();
};

export const fetchAssignmentSubmissions = async (assignmentId, schoolId = null) => {
    const params = schoolId ? `?school_id=${schoolId}` : '';
    const response = await fetch(`${API_BASE_URL}/admin/hpc/assignments/${assignmentId}/submissions${params}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch submissions: ${response.statusText}`);
    return response.json();
};

// ─── Coverage ────────────────────────────────────────────────────────────────

export const fetchHPCCoverage = async (params = {}, schoolId = null) => {
    if (schoolId) params = { ...params, school_id: schoolId };
    const query = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/admin/hpc/coverage${query ? `?${query}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch coverage: ${response.statusText}`);
    return response.json();
};

// ─── Teachers List (for dropdowns) ───────────────────────────────────────────

export const fetchTeachersList = async (schoolId = null) => {
    const params = schoolId ? `&school_id=${schoolId}` : '';
    const response = await fetch(`${API_BASE_URL}/admin/hpc/teachers?limit=200${params}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch teachers list: ${response.statusText}`);
    const data = await response.json();
    return data.teachers || [];
};

// ─── Profile Picture ─────────────────────────────────────────────────────────

export const fetchStudentProfilePicture = async (studentId) => {
    try {
        const accessToken = localStorage.getItem("accessToken");
        // Use the admin users endpoint to get student info with profile picture
        const response = await fetch(`${API_BASE_URL}/admin/users/${studentId}`, {
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) return getDefaultProfileImage();

        const userData = await response.json();
        if (!userData?.profile_picture_url) return getDefaultProfileImage();

        const imgUrl = API_BASE_URL + userData.profile_picture_url;

        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = "Anonymous";
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    resolve(canvas.toDataURL('image/jpeg', 0.95));
                } catch {
                    resolve(getDefaultProfileImage());
                }
            };
            img.onerror = () => resolve(getDefaultProfileImage());
            img.src = imgUrl;
        });
    } catch {
        return getDefaultProfileImage();
    }
};

function getDefaultProfileImage() {
    const canvas = document.createElement('canvas');
    canvas.width = 100;
    canvas.height = 100;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(0, 0, 100, 100);
    ctx.strokeStyle = '#4B5563';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, 98, 98);
    ctx.fillStyle = '#6B7280';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Student', 50, 50);
    return canvas.toDataURL('image/png');
}

// ─── Utility ─────────────────────────────────────────────────────────────────

export const displayDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "Invalid Date";
        return date.toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
        return "Error Date";
    }
};
